import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  INVOICE_BASIS_LABELS,
  REVERSE_CHARGE_TEXT,
  bcInvoiceStatusLabel,
  canOpenContract,
  customerInvoiceLines,
  formatDate,
  formatDateLong,
  formatHours,
  formatPercent,
  type InvoiceBilagResponse,
  type InvoicePdfResponse,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { invoiceCurrency, invoiceCustomerName, unitLabel } from '@/lib/invoice';
import { contractPath } from '@/lib/paths';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import { InvoiceStatusBadge, Money, PageHeader, PermittedLink } from '@/components/domain';
import type { InvoiceDetail } from '@/types';

/**
 * Kan browseren vise en PDF indlejret?
 *
 * `navigator.pdfViewerEnabled` er den standardiserede måde at spørge, og den er svaret på et
 * konkret problem: Chrome og Edge har hver sin indstilling for, om PDF'er skal **åbnes** eller
 * **hentes** ("Download PDFs instead of automatically opening them", eller
 * Intune-politikken `AlwaysOpenPdfExternally`). Er den sat til at hente, viser en `<object>`
 * sit fallback, og en `<iframe>` starter en download ved hver sidevisning — en fil i
 * Overførsler, hver gang man åbner en faktura.
 *
 * Derfor spørges der først. Kan browseren ikke vise PDF'en, indlejres den slet ikke; kortet
 * siger hvorfor og giver de to veje, der virker.
 *
 * Kender browseren ikke feltet, antages den at kunne — et preview, der skjuler sig selv på et
 * ukendt felt, er værre end et, der prøver.
 */
function pdfViewerEnabled(): boolean {
  return (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled ?? true;
}

/**
 * Base64 → blob-URL til en PDF. Svaret er JSON, fordi /invoices går gennem demo-hooken, som kun
 * svarer JSON; browseren skal have en URL for at kunne vise PDF'en.
 *
 * Delt af de to previews — fakturaen og bilaget kommer begge fra Business Central som PDF.
 */
function usePdfUrl(base64: string | undefined): string | null {
  const url = useMemo(() => {
    if (!base64) return null;

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  }, [base64]);

  /* Blob-URL'er lever, indtil de bliver trukket tilbage. Uden dette lækker hver sidevisning én. */
  useEffect(() => {
    if (!url) return;
    return () => URL.revokeObjectURL(url);
  }, [url]);

  return url;
}

/**
 * PDF'en indlejret i siden — eller, når browseren ikke vil vise den, de to veje der virker.
 *
 * `<iframe>` og ikke `<object>`: den sidste viser ikke en `blob:`-URL i Chrome, men falder til sit
 * alternative indhold, også når filen er en gyldig PDF. Det var ikke filen, der var noget i vejen
 * med — `%PDF-1.7 … %%EOF`, og base64'en kom retur byte for byte.
 *
 * Der er ingen fallback inde i en iframe, og det er med vilje: kan browseren ikke vise PDF'er,
 * når vi aldrig hertil — se `pdfViewerEnabled`.
 */
function PdfFrame({ url, fileName, title }: { url: string; fileName: string; title: string }) {
  /* Spurgt én gang. Ændrer brugeren indstillingen, gælder den fra næste genindlæsning. */
  const [canEmbed] = useState(pdfViewerEnabled);

  if (canEmbed) {
    return (
      <iframe src={url} title={title} className="h-[36rem] w-full rounded-md border border-line" />
    );
  }

  return (
    <div className="rounded-md border border-line bg-canvas p-5">
      <p className="text-sm font-medium">Din browser viser ikke PDF'er i siden</p>
      <p className="mt-1 text-xs text-ink-muted">
        Den er sat til at hente PDF'er i stedet for at åbne dem. Filen er hentet fra Business
        Central og er klar — den kan bare ikke vises her. Slå det til under{' '}
        <span className="font-mono">Indstillinger → Privatliv og sikkerhed → Indhold</span> →
        PDF-dokumenter, eller brug de to knapper her.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <a href={url} target="_blank" rel="noreferrer">
          <Button size="sm" variant="secondary">
            Åbn i ny fane
          </Button>
        </a>
        <a href={url} download={fileName}>
          <Button size="sm" variant="ghost">
            Hent {fileName}
          </Button>
        </a>
      </div>
    </div>
  );
}

/**
 * Fakturaen som PDF, gengivet af Business Central.
 *
 * **BC's eget dokument og ikke Codepass' tegning af det** — samme greb som bilaget nedenunder.
 * Tabellen, der stod her før, var en gengivelse af BC's linjer, og to gengivelser af det samme
 * dokument kan vise hver sit. Nu er det den rapport, BC selv laver af fakturaen: på en kladde
 * BC's udkast-layout, og på en bogført faktura den endelige. Se `fetchBcInvoicePdf` i API'et.
 *
 * **Tabellen er tilbage, når BC ikke kan levere PDF'en** — BC er nede, eller fakturaen findes
 * ikke dér. Den står da med en besked om, at det er Codepass' visning af linjerne og ikke
 * dokumentet fra BC. Et tomt kort ville skjule en faktura, vi faktisk kender linjerne på.
 */
function InvoicePdfPreview({
  invoiceId,
  status,
  payment,
  fallback,
}: {
  invoiceId: string | undefined;
  status: InvoiceDetail['status'];
  /** The invoice's payment - BC's status and the ledger's settlement. See `InvoiceStatusBadge`. */
  payment: InvoiceDetail;
  fallback: React.ReactNode;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice', invoiceId, 'pdf'],
    queryFn: () => api.get<InvoicePdfResponse>(`/invoices/${invoiceId}/pdf`),
    enabled: Boolean(invoiceId),
    /* Samme som bilaget: hvert kald er en tur til BC, og dokumentet ændrer sig ikke imens. */
    staleTime: 5 * 60 * 1000,
    /* Ét nyt forsøg og ikke tre: tabellen står klar, og den skal ikke vente på BC i et minut. */
    retry: 1,
  });

  const url = usePdfUrl(data?.content_base64);

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>Faktura-preview</CardTitle>
        <div className="flex flex-wrap items-center gap-3">
          {data && url ? (
            <>
              <span className="text-xs text-ink-muted">
                {status === 'draft' ? 'Kladde i Business Central' : 'Fra Business Central'}
              </span>
              <a
                href={url}
                download={data.file_name}
                className="text-xs text-brand hover:underline"
              >
                Hent PDF
              </a>
            </>
          ) : null}
          <InvoiceStatusBadge status={status} payment={payment} />
        </div>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton className="h-96" />
        ) : error || !data || !url ? (
          <>
            <Alert tone="warning" className="mb-3">
              {error instanceof ApiError ? error.message : 'Fakturaen kunne ikke hentes fra BC.'}{' '}
              Nedenfor står fakturaens linjer, som Codepass har dem — ikke dokumentet fra Business
              Central.
            </Alert>
            {fallback}
          </>
        ) : (
          <PdfFrame url={url} fileName={data.file_name} title={`Faktura: ${data.file_name}`} />
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Bilaget som PDF, hentet fra Business Central.
 *
 * **Filen selv og ikke en gengivelse.** Previewet viser den vedhæftede fil, kunden får med
 * regningen — se `fetchBcInvoiceBilag` i API'et. En tabel ved siden af ville være en anden
 * gengivelse af det samme dokument, og to gengivelser kan vise hver sit.
 */
function BilagPreview({ invoiceId }: { invoiceId: string | undefined }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice', invoiceId, 'bilag'],
    queryFn: () => api.get<InvoiceBilagResponse>(`/invoices/${invoiceId}/bilag`),
    enabled: Boolean(invoiceId),
    /* Filen i BC ændrer sig ikke, mens siden er åben, og hvert kald er en tur til BC. */
    staleTime: 5 * 60 * 1000,
  });

  const url = usePdfUrl(data?.content_base64);

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>Bilag-preview</CardTitle>
        {data ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-ink-muted">
              {data.source === 'bc' ? 'Vedhæftet i Business Central' : 'Ikke vedhæftet i BC'}
            </span>
            {url ? (
              <a
                href={url}
                download={data.file_name}
                className="text-xs text-brand hover:underline"
              >
                Hent PDF
              </a>
            ) : null}
          </div>
        ) : null}
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton className="h-96" />
        ) : error || !data || !url ? (
          <p className="text-xs text-ink-muted">
            Bilaget kunne ikke hentes. Fakturaen findes måske ikke i Business Central, eller
            forbindelsen svarede ikke.
          </p>
        ) : (
          <>
            {/*
              Mærkatet står kun, når filen ikke kom fra BC. Så er det, man ser, hvad Codepass
              ville have sendt — og ikke hvad kunden har. Den forskel må ikke stå ulæst.
            */}
            {data.source === 'local' && data.local_reason ? (
              <Alert tone="warning" className="mb-3">
                {data.local_reason}
              </Alert>
            ) : null}

            <PdfFrame url={url} fileName={data.file_name} title={`Bilag: ${data.file_name}`} />
          </>
        )}
      </CardBody>
    </Card>
  );
}

interface BillingItem {
  id: string;
  /** Null uden et skema at pege paa - grundlaget udledes, se invoice-hours-view.ts. */
  invoice_line_id: string | null;
  /** Null naar linjen ikke kunne genkendes som en opgave i OnePro. */
  hour_id: string | null;
  milestone_id: string | null;
  hour: {
    id: string;
    /** Null paa en linje uden en genkendt opgave: kun opgaven baerer en dato. */
    work_date: string | null;
    duration_hours: number;
    description: string | null;
    hourly_rate_excl_vat: number | null;
    consultant: { name: string } | null;
  } | null;
  milestone: { id: string; title: string; billing_amount_excl_vat: number } | null;
}

export function InvoiceDetailPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const [showBasis, setShowBasis] = useState(false);

  const {
    data: invoice,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${id}`),
    enabled: Boolean(id),
  });

  const { data: basis } = useQuery({
    queryKey: ['invoice', id, 'billing-items'],
    /**
     * `exact` siger om opgaverne blev slaaet op paa id eller genkendt paa linjeteksten. Kun
     * det andet tilfaelde skal maerkes - se noten i panelet. Feltet er valgfrit, saa et svar
     * fra en aeldre server ikke faar panelet til at paastaa noget.
     */
    /*
     * `groups` laeses ikke laengere. Feltet er stadig i svaret - det er bilagets grupper, og
     * serveren bruger dem, naar den skal gengive PDF'en - men previewet viser nu filen fra BC
     * og ikke en tabel bygget af dem. Se `BilagPreview`.
     */
    queryFn: () =>
      api.get<{ data: BillingItem[]; exact?: boolean }>(`/invoices/${id}/billing-items`),
    enabled: Boolean(id),
  });

  if (isLoading) return <Skeleton className="h-64" />;
  if (error || !invoice) return <Alert tone="error">Kunne ikke hente fakturaen.</Alert>;

  const reverseCharge = invoice.vat_amount === 0 && invoice.amount_excl_vat > 0;
  /*
   * Kunde og valuta ligger paa kontrakten for en Codepass-faktura og paa fakturaen selv for
   * en faktura laest fra BC. Valget sker ét sted, saa preview og detaljer ikke kan komme til
   * at vise hver sin valuta.
   */
  const currency = invoiceCurrency(invoice);
  const customerName = invoiceCustomerName(invoice);

  /**
   * Fakturaen som kunden får den: **én linje pr. aftale**.
   *
   * Samme funktion som byggede de linjer, der blev sendt til Business Central — se
   * `customerInvoiceLines`. Previewet er dermed ikke en gengivelse af fakturaen, men den samme
   * beregning på de samme linjer, og de to kan ikke nå hver sit resultat.
   *
   * Opdelingen pr. kontraktlinje er ikke væk: den står i Bilag-previewet nedenunder, som er
   * specifikationen af opgaverne bag beløbet.
   *
   * `known` skiller "uden aftale" fra "det ved vi ikke". En faktura oprettet direkte i BC, eller
   * en hvis sidespor er væk efter en genstart, bærer ingen aftale på linjerne; de står da som
   * BC's egne linjer uden overskrift frem for under en, der ville påstå at arbejdet ikke hører
   * til nogen aftale.
   */
  const customerLines = customerInvoiceLines(invoice.lines);

  /**
   * Momssatsen fakturaen faktisk bærer — linjernes egen fra Business Central.
   *
   * Null når linjerne ikke er enige, eller når der ingen linjer er: så står 'Moms' uden sats
   * frem for at vælge den ene af to. Beløbet er BC's eget uanset hvad.
   */
  const vatRates = new Set(invoice.lines.map((line) => line.vat_rate).filter((rate) => rate > 0));
  const vatRate = vatRates.size === 1 ? [...vatRates][0] : null;

  /**
   * Kontaktpersonerne på fakturaens aftaler, komma-separeret.
   *
   * Dubletter falder fra: to aftaler med den samme kontaktperson er ét navn på fakturaen, ikke
   * det samme navn to gange. Tom streng bliver til null, så feltet udelades frem for at stå med
   * en tom værdi — aftaler uden kontaktperson i CRM findes.
   */
  const contactNames =
    [
      ...new Set(
        (invoice.contracts ?? [])
          .map((row) => row.contact_name?.trim())
          .filter((name): name is string => Boolean(name)),
      ),
    ].join(', ') || null;

  return (
    <>
      {/*
        No actions here. Codepass makes the draft; the bookkeeping reviews it, posts it and sends
        it to the customer in Business Central - and deletes it there, if it is not to be sent
        (decided 06-10-2026). The status below follows BC.
      */}
      <PageHeader
        title={invoice.bc_invoice_id ?? invoice.invoice_no_proposal ?? 'Fakturaudkast'}
        description={[invoice.contract?.title, customerName].filter(Boolean).join(' — ')}
      />

      {/*
        Vises paa enhver faktura der baerer en BC-fejl, ikke kun paa dem med status Fejlet.
        Et udkast oprettes nu i BC allerede naar det gemmes, og bliver det afvist, staar
        fakturaen stadig som Kladde i Codepass - grundlaget er der, det er kladden i BC der
        mangler. Var beskeden bundet til status Fejlet, var det den eneste fejl i flowet man
        ikke kunne se.
      */}
      {invoice.bc_error_message ? (
        <Alert tone="error" className="mb-4" title="Kladden blev ikke oprettet i Business Central">
          {invoice.bc_error_message}
          <p className="mt-1 text-xs">Antal forsøg: {invoice.bc_attempt_count}</p>
        </Alert>
      ) : null}

      {/*
       * `items-start` og ikke gitterets standard `stretch`.
       *
       * Faktura-preview og hoejre kolonne staar i samme raekke, og med stretch faar de begge
       * raekkens hoejde - altsaa den hoejeste af de to. Foldes Faktureringsgrundlag ud, vokser
       * hoejre kolonne, og previewet blev straekket med og fik tomt rum under sin sidste linje.
       * Med start beholder hvert kort sin egen hoejde.
       */}
      <div className="grid items-start gap-4 lg:grid-cols-3">
        {/*
         * Venstre spalte er **ét** gitterelement med to kort i, ikke to elementer i gitteret.
         *
         * Laa de to som hver sit element, havnede bilaget i raekke to, og raekke ét er lige saa
         * hoej som sit hoejeste element - hoejre kolonne. Naar Faktureringsgrundlag blev foldet
         * ud, voksede den, og bilaget blev skubbet ned med den. Som én spalte flyder de to kort
         * for sig, og hoejre kolonne kan vokse uden at roere dem.
         */}
        <div className="space-y-4 lg:col-span-2">
          {/*
            Faktura-previewet: BC's egen PDF af fakturaen - se `InvoicePdfPreview`. Tabellen
            herunder er kun det, der vises, naar BC ikke kan levere den: fakturaens linjer, som
            Codepass har dem. Kun opgavetitler, aldrig interne timebeskrivelser.
          */}
          <InvoicePdfPreview
            invoiceId={id}
            status={invoice.status}
            payment={invoice}
            fallback={
              <div className="rounded-md border border-line p-5">
                <div className="flex justify-between">
                  <div>
                    <p className="text-sm font-semibold">OneCode ApS</p>
                    <p className="text-xs text-ink-muted">Faktura</p>
                  </div>
                  <div className="text-right text-xs">
                    {/*
                      Fakturanummeret staar øverst: det er BC's eget, og det er det, en faktura
                      omtales med. Kladder i BC har det også.
                    */}
                    {invoice.bc_invoice_id ? <p>Fakturanr.: {invoice.bc_invoice_id}</p> : null}
                    <p>Fakturadato: {formatDateLong(invoice.created_at)}</p>
                    <p>Forfaldsdato: {formatDateLong(invoice.due_date)}</p>
                    {invoice.period_start ? (
                      <p>
                        Periode: {formatDate(invoice.period_start)} –{' '}
                        {formatDate(invoice.period_end)}
                      </p>
                    ) : null}
                    {/*
                      Hverken referencen (`externalDocumentNumber`) eller kundenummeret staar her.
                      De sendes til BC, men de er bogholderiets noegler og ikke noget, kunden skal
                      laese paa fakturaen. Referencen kan ses i panelet ved siden af.
                    */}
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-xs text-ink-muted">Faktureres til</p>
                  <p className="text-sm font-medium">{customerName ?? '—'}</p>
                  {/*
                    Kontaktpersonen på aftalen. Dækker fakturaen flere aftaler, står de
                    komma-separeret — én linje frem for én pr. aftale, fordi det er ét felt på
                    fakturaen og ikke en liste kunden skal læse igennem.
                  */}
                  {contactNames ? (
                    <p className="mt-1 text-xs">
                      <span className="text-ink-muted">Kontaktperson: </span>
                      {contactNames}
                    </p>
                  ) : null}
                </div>

                <table className="mt-5 w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-xs text-ink-muted">
                      <th className="py-1 text-left font-medium">Beskrivelse</th>
                      <th className="py-1 text-right font-medium">Antal</th>
                      <th className="py-1 text-right font-medium">Enhedspris</th>
                      <th className="py-1 text-right font-medium">Beløb</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/*
                      **Én raekke pr. aftale**, ikke én pr. kontraktlinje.

                      Kunden skal se hvad aftalen koster - antal, enhedspris og beloeb - og ikke
                      hvordan arbejdet var delt op internt. Opdelingen er ikke vaek: den staar i
                      Bilag-previewet lige nedenunder, som er specifikationen af opgaverne.

                      Tallene lægges sammen af `summarizeInvoiceLines`, saa fakturaen og bilaget
                      ikke kan naa hver sin sum for den samme aftale.
                    */}
                    {customerLines.map((line, index) => (
                      <tr
                        key={`${line.contract?.id ?? 'none'}-${index}`}
                        className="border-b border-line/60 align-bottom"
                      >
                        <td className="py-2">
                          {line.known ? (
                            <>
                              <span className="text-xs uppercase tracking-wide text-ink-muted">
                                Kontraktoplysninger
                              </span>
                              <span className="mt-0.5 block text-sm font-semibold">
                                {line.description}
                              </span>
                            </>
                          ) : (
                            line.description
                          )}
                        </td>
                        <td className="py-2 text-right tabular">
                          {/*
                           * unitLabel og ikke UNIT_LABELS: en linje fra BC baerer BC's egen
                           * enhedskode (TIME, STK), som ikke findes i Codepass' enheder. Et raat
                           * opslag ville skrive "1 undefined" i tabellen. Uden en faelles enhed
                           * staar antallet alene.
                           */}
                          {line.unit === 'hours'
                            ? `${formatHours(line.quantity)} ${unitLabel(line.unit)}`
                            : line.unit
                              ? `${line.quantity} ${unitLabel(line.unit)}`
                              : formatHours(line.quantity)}
                        </td>
                        <td className="py-2 text-right">
                          <Money value={line.unit_price_excl_vat} currency={currency} />
                        </td>
                        <td className="py-2 text-right">
                          <Money value={line.total_excl_vat} currency={currency} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-4 ml-auto w-64 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Subtotal</span>
                    <Money value={invoice.amount_excl_vat} currency={currency} />
                  </div>
                  <div className="flex justify-between">
                    {/*
                      Momssatsen er linjernes egen fra BC - ikke 25 skrevet i UI'et. Den sats,
                      BC regner med, staar paa kundekortet dér, og et fast tal her ville vise en
                      anden procent end den, kunden faktisk faktureres med. Falder den tilbage,
                      er det kun naar linjerne ikke er enige - da staar 'Moms' uden sats.
                    */}
                    <span className="text-ink-muted">
                      {reverseCharge || vatRate === null
                        ? 'Moms'
                        : `Moms (${formatPercent(vatRate)})`}
                    </span>
                    <Money value={invoice.vat_amount} currency={currency} />
                  </div>
                  <div className="flex justify-between border-t border-line pt-1 font-semibold">
                    <span>Total</span>
                    <Money value={invoice.amount_incl_vat} currency={currency} />
                  </div>
                </div>

                {reverseCharge ? (
                  <p className="mt-4 text-xs text-ink-muted">{REVERSE_CHARGE_TEXT}</p>
                ) : null}
              </div>
            }
          />

          {/*
           * Bilag-previewet: **filen fra Business Central**, ikke en gengivelse af den.
           *
           * Her stod før en tabel, der tegnede kontraktlinjer, opgaver og underopgaver af
           * `basis.groups`. To ting var galt med det. Den viste de interne opgavenavne fra
           * OnePro til en kunde, og den var en anden gengivelse af det samme dokument som
           * PDF'en i BC — to gengivelser, der kunne drive fra hinanden, hvor den forkerte var
           * den, ingen kunne se. Nu hentes den vedhæftede fil selv, og bilaget findes kun ét
           * sted. Se `fetchBcInvoiceBilag`.
           *
           * Det står stadig som sit eget kort under fakturaen og ikke inde i den printvenlige
           * ramme: de to er hver sit dokument, ikke ét.
           */}
          <BilagPreview invoiceId={id} />
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Detaljer</CardTitle>
            </CardHeader>
            <CardBody>
              <dl className="space-y-2 text-sm">
                <Row
                  label="Grundlag"
                  value={invoice.invoice_basis ? INVOICE_BASIS_LABELS[invoice.invoice_basis] : '—'}
                />
                {/*
                 * En faktura laest fra BC har ingen kontrakt bag sig. Raekken bliver staaende
                 * med en tankestreg frem for at forsvinde: at feltet er tomt, er en oplysning.
                 */}
                <Row
                  label="Kontrakt"
                  value={
                    invoice.contract ? (
                      <PermittedLink
                        to={contractPath(invoice.contract)}
                        allowed={canOpenContract(user, invoice.contract)}
                        className="hover:underline"
                      >
                        {invoice.contract.title}
                      </PermittedLink>
                    ) : (
                      '—'
                    )
                  }
                />
                {/*
                 * BC's egen status, oversat. Den staar ved siden af Codepass' status, fordi
                 * mapningen taber en nuance: baade Open og Paid bliver `posted` her.
                 */}
                {invoice.bc_status ? (
                  <Row label="Status i BC" value={bcInvoiceStatusLabel(invoice.bc_status)} />
                ) : null}
                {invoice.milestone ? <Row label="Milepæl" value={invoice.milestone.title} /> : null}
                <Row
                  label="Oprettet"
                  value={`${formatDate(invoice.created_at)}${
                    invoice.generated_by === 'agent' ? ' (automatisk)' : ''
                  }`}
                />
                {/*
                 * The day it was posted in BC - which is the day it went to the customer: the
                 * bookkeeping posts and sends in one step. Empty on a draft.
                 */}
                {invoice.posting_date ? (
                  <Row label="Sendt" value={formatDate(invoice.posting_date)} />
                ) : null}
                {/*
                 * Bufferen bag linjerne — intern. Den er lagt ud på linjernes antal, så BC har
                 * ingen kolonne til den; tallet kommer fra Codepass' eget sidespor i
                 * `codepass_invoices`.
                 *
                 * **0 kr og ikke en tankestreg**, også når sidesporet ikke kender fakturaen — en
                 * faktura tastet direkte i BC, eller en oprettet før grundlaget blev gemt. De
                 * fakturaer har ingen buffer at vise, og 0 kr er dét, ikke et manglende tal.
                 */}
                <Row
                  label="Buffer"
                  value={<Money value={invoice.buffer_excl_vat ?? 0} currency={currency} />}
                />
                <Row label="BC-ID" value={invoice.bc_invoice_id ?? '—'} />
              </dl>

              {/*
                Referencen og ikke en note. Feltet er BC's `externalDocumentNumber`, som Codepass
                skriver aftalen og grundlaget i - `ONC-1072/HOURS`, se `invoiceReference` - eller
                perioden paa en abonnementsfaktura, `PERIODE: OKTOBER 2026`, se `periodReference`.
                Det stod som 'Intern note', fordi kolonnen hedder `notes` i svaret; overskriften
                siger nu, hvad der faktisk staar der.
              */}
              {invoice.notes ? (
                <div className="mt-3 rounded-md bg-canvas p-3 text-xs">
                  <p className="font-medium">Reference</p>
                  <p className="mt-1 text-ink-muted">{invoice.notes}</p>
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Faktureringsgrundlag</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => setShowBasis(!showBasis)}>
                {showBasis ? 'Skjul' : 'Vis'}
              </Button>
            </CardHeader>
            {showBasis ? (
              <CardBody>
                <p className="mb-2 text-xs text-ink-muted">
                  Intern visning. De underliggende timer indgår ikke i det kunden ser.
                  {/*
                   * `exact: false` betyder at opgaverne er genkendt paa linjeteksten frem for
                   * slaaet op paa id. Timetallene er fakturaens egne uanset hvad; det er
                   * koblingen til konsulent og opgave der er udledt, og det skal kunne ses.
                   */}
                  {basis && basis.exact === false ? (
                    <span className="block">
                      Opgaverne er genkendt ud fra fakturalinjernes tekst, ikke slået op på id.
                    </span>
                  ) : null}
                </p>
                {!basis ? (
                  <Skeleton className="h-20" />
                ) : basis.data.length === 0 ? (
                  <p className="text-xs text-ink-muted">Intet registreret grundlag.</p>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th className="w-[100px]">Dato</Th>
                        <Th>Konsulent</Th>
                        <Th className="text-right">Timer</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {basis.data
                        .filter((item) => item.hour)
                        .map((item) => (
                          <Tr key={item.id}>
                            <Td className="tabular text-xs">{formatDate(item.hour?.work_date)}</Td>
                            <Td className="text-xs">
                              {item.hour?.consultant?.name ?? '—'}
                              {item.hour?.description ? (
                                <span className="block text-ink-muted">
                                  {item.hour.description}
                                </span>
                              ) : null}
                            </Td>
                            <Td className="text-right tabular text-xs">
                              {formatHours(item.hour?.duration_hours ?? null)}
                            </Td>
                          </Tr>
                        ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            ) : null}
          </Card>
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
